import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { projectsAPI } from '../services/api'
import {
  User, LogOut, AlertCircle, Briefcase, Clock, CheckCircle,
  Calendar, Euro, Upload, Eye, Download, ArrowRight, Filter,
  ChevronDown, RefreshCw, MessageSquare, Star, ArrowLeft
} from 'lucide-react'

const MyActiveProjectsPage = ({ onNavigate } = {}) => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [projects, setProjects] = useState([])
  const [stats, setStats] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => {
    fetchProjects()
  }, [statusFilter])

  const fetchProjects = async () => {
    try {
      setLoading(true)
      const response = await projectsAPI.getMyActiveProjects({
        status: statusFilter,
        limit: 50
      })

      setProjects(response.data.projects || [])
      setStats(response.data.stats || {})

    } catch (error) {
      console.error('Error fetching active projects:', error)
      setError('Er is een fout opgetreden bij het laden van je opdrachten')
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    await fetchProjects()
    setRefreshing(false)
  }

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  const getStatusColor = (status) => {
    const colors = {
      creator_selected: 'text-blue-400 bg-emerald-50',
      in_progress: 'text-yellow-600 bg-yellow-50',
      delivered: 'text-[#7c3aed] bg-purple-50',
      under_review: 'text-orange-400 bg-orange-50',
      revision_requested: 'text-red-500 bg-red-50',
      approved: 'text-emerald-600 bg-emerald-50',
      completed: 'text-emerald-400 bg-emerald-50'
    }
    return colors[status] || 'text-gray-400 bg-gray-100'
  }

  const getStatusText = (status) => {
    const texts = {
      creator_selected: 'Geselecteerd',
      in_progress: 'Bezig',
      delivered: 'Opgeleverd',
      under_review: 'In beoordeling',
      revision_requested: 'Revisie nodig',
      approved: 'Goedgekeurd',
      completed: 'Voltooid'
    }
    return texts[status] || status
  }

  const getNextAction = (project) => {
    switch (project.status) {
      case 'creator_selected':
        return {
          text: 'Begin met werken',
          icon: ArrowRight,
          color: 'bg-[#3EB489] hover:bg-[#359E78]'
        }
      case 'in_progress':
        return {
          text: 'Upload content',
          icon: Upload,
          color: 'bg-purple-600 hover:bg-purple-700'
        }
      case 'delivered':
        return {
          text: 'Wacht op beoordeling',
          icon: Clock,
          color: 'bg-orange-600 cursor-not-allowed opacity-50',
          disabled: true
        }
      case 'under_review':
        return {
          text: 'In beoordeling',
          icon: Eye,
          color: 'bg-orange-600 cursor-not-allowed opacity-50',
          disabled: true
        }
      case 'revision_requested':
        return {
          text: 'Upload revisie',
          icon: Upload,
          color: 'bg-red-600 hover:bg-red-700'
        }
      case 'approved':
        return {
          text: 'Wacht op betaling',
          icon: Euro,
          color: 'bg-green-600 cursor-not-allowed opacity-50',
          disabled: true
        }
      case 'completed':
        return {
          text: 'Voltooid',
          icon: CheckCircle,
          color: 'bg-emerald-600 cursor-not-allowed opacity-50',
          disabled: true
        }
      default:
        return {
          text: 'Bekijk details',
          icon: ArrowRight,
          color: 'bg-gray-600 hover:bg-gray-700'
        }
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  const getDaysUntilDeadline = (deadline) => {
    const now = new Date()
    const deadlineDate = new Date(deadline)
    const diffTime = deadlineDate - now
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays < 0) return { text: 'Verlopen', color: 'text-red-500' }
    if (diffDays <= 3) return { text: `${diffDays} dagen`, color: 'text-red-500' }
    if (diffDays <= 7) return { text: `${diffDays} dagen`, color: 'text-orange-400' }
    return { text: `${diffDays} dagen`, color: 'text-emerald-600' }
  }

  if (loading) {
    return (
      <div className="font-['Inter'] min-h-screen flex items-center justify-center bg-white text-[#0F172A]">
        <div className="w-8 h-8 border-2 border-[#1D8BF8] border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="font-['Inter'] min-h-screen flex flex-col bg-white text-[#0F172A] overflow-x-hidden">

      {/* Header */}
      <header className="w-full flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-100 sticky top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl">
        <div className="flex items-center gap-2">
          <Link to="/dashboard" className="md:hidden p-2.5 -ml-2 rounded-lg hover:bg-gray-100 transition" aria-label="Terug naar dashboard">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <Link to="/" className="text-2xl font-black tracking-tighter text-[#0F172A]">
            UGC<span className="text-[#3EB489]">4you</span>
          </Link>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="text-right hidden md:block">
              <div className="text-sm font-semibold">{user.firstName}</div>
              <div className="text-xs text-[#475569]">Creator</div>
            </div>
            <div className="w-10 h-10 bg-[#3EB489] rounded-full flex items-center justify-center">
              {user.profilePictureUrl ? (
                <img src={user.profilePictureUrl} alt="Profile" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <User className="w-5 h-5 text-white" />
              )}
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="px-2 sm:px-4 py-2 text-sm font-medium hover:text-[#3EB489] transition flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Uitloggen</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {/* Header Section — stapelt onder sm zodat de knoppen niet uit beeld lopen (P1) */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-8">
            <div className="min-w-0">
              <h1 className="text-2xl md:text-4xl font-black mb-2">Mijn Actieve Opdrachten</h1>
              <p className="text-[#475569] text-base md:text-lg">
                Overzicht van al je lopende projecten en opdrachten
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3 sm:gap-4 sm:flex-shrink-0">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="px-4 py-2 min-h-[44px] sm:min-h-0 bg-gray-100 hover:bg-gray-200 rounded-lg transition flex items-center gap-2 disabled:opacity-50 whitespace-nowrap"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                Vernieuwen
              </button>

              <Link
                to="/dashboard"
                className="px-4 py-2 min-h-[44px] sm:min-h-0 inline-flex items-center bg-[#3EB489] hover:bg-[#359E78] text-white rounded-lg transition whitespace-nowrap"
              >
                Terug naar Dashboard
              </Link>
            </div>
          </div>

          {error && (
            <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-500 mr-3" />
                <p className="text-sm text-red-500">{error}</p>
              </div>
            </div>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-3 md:gap-6 mb-8">
            <div className="relative group">
              <div className="relative p-3 md:p-6 rounded-2xl bg-white shadow-md border border-gray-200">
                <div className="flex items-center justify-between mb-2 md:mb-4">
                  <div className="hidden md:flex w-12 h-12 bg-emerald-50 rounded-xl items-center justify-center">
                    <Briefcase className="w-6 h-6 text-[#3EB489]" />
                  </div>
                  <span className="text-xl md:text-3xl font-bold text-[#0F172A] break-all">{stats.activeProjects || 0}</span>
                </div>
                <p className="text-xs md:text-sm font-medium text-[#475569]">Actieve Opdrachten</p>
              </div>
            </div>

            <div className="relative group">
              <div className="relative p-3 md:p-6 rounded-2xl bg-white shadow-md border border-gray-200">
                <div className="flex items-center justify-between mb-2 md:mb-4">
                  <div className="hidden md:flex w-12 h-12 bg-emerald-50 rounded-xl items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-emerald-600" />
                  </div>
                  <span className="text-xl md:text-3xl font-bold text-[#0F172A] break-all">{stats.completedProjects || 0}</span>
                </div>
                <p className="text-xs md:text-sm font-medium text-[#475569]">Voltooid</p>
              </div>
            </div>

            <div className="relative group">
              <div className="relative p-3 md:p-6 rounded-2xl bg-white shadow-md border border-gray-200">
                <div className="flex items-center justify-between mb-2 md:mb-4">
                  <div className="hidden md:flex w-12 h-12 bg-yellow-50 rounded-xl items-center justify-center">
                    <Euro className="w-6 h-6 text-yellow-600" />
                  </div>
                  <span className="text-xl md:text-3xl font-bold text-[#0F172A] break-all">{stats.totalEarnings || 0}</span>
                </div>
                <p className="text-xs md:text-sm font-medium text-[#475569]">Totaal Verdiend</p>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="mb-6">
            <div className="flex flex-wrap items-center gap-3 sm:gap-4">
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="appearance-none bg-gray-100 border border-gray-200 rounded-lg px-4 py-2 pr-10 text-[#0F172A] focus:outline-none focus:border-[#1D8BF8]"
                >
                  <option value="all">Alle statussen</option>
                  <option value="creator_selected">Geselecteerd</option>
                  <option value="in_progress">Bezig</option>
                  <option value="delivered">Opgeleverd</option>
                  <option value="under_review">In beoordeling</option>
                  <option value="revision_requested">Revisie nodig</option>
                  <option value="approved">Goedgekeurd</option>
                  <option value="completed">Voltooid</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#475569] pointer-events-none" />
              </div>
              <span className="text-sm text-[#475569]">
                {projects.length} {projects.length === 1 ? 'opdracht' : 'opdrachten'} gevonden
              </span>
            </div>
          </div>

          {/* Projects List */}
          {projects.length === 0 ? (
            <div className="relative">
              <div className="relative rounded-2xl bg-white shadow-md border border-gray-200">
                <div className="text-center py-20">
                  <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-2">Geen actieve opdrachten</h3>
                  <p className="text-[#475569] mb-6">
                    Je hebt momenteel geen actieve opdrachten.
                  </p>
                  <button
                    onClick={() => onNavigate ? onNavigate('projects') : navigate('/projects')}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#3EB489] text-white font-bold rounded-lg hover:bg-[#359E78] transition"
                  >
                    <Briefcase className="w-5 h-5" />
                    Zoek nieuwe opdrachten
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {projects.map((project) => {
                const nextAction = getNextAction(project)
                const deadline = getDaysUntilDeadline(project.deadline)

                return (
                  <div key={project.id} className="relative group">
                    <div className="relative rounded-2xl bg-white shadow-md border border-gray-200 hover:border-gray-300 transition-all">
                      <div className="p-4 sm:p-6">
                        {/* Kop: stapelt onder sm; tekstkolom min-w-0 zodat de pill en het budget niet overlappen (P1) */}
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                              <h3 className="text-xl font-bold break-words">{project.title}</h3>
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(project.status)}`}>
                                {getStatusText(project.status)}
                              </span>
                            </div>
                            <p className="text-[#475569] mb-3 line-clamp-3 break-words">{project.description}</p>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#475569]">
                              <span className="flex items-center gap-1 whitespace-nowrap">
                                <Calendar className="w-4 h-4" />
                                Deadline: {formatDate(project.deadline)}
                              </span>
                              <span className={`flex items-center gap-1 whitespace-nowrap ${deadline.color}`}>
                                <Clock className="w-4 h-4" />
                                {deadline.text} resterend
                              </span>
                            </div>
                          </div>

                          <div className="text-left sm:text-right sm:flex-shrink-0">
                            <div className="text-xl md:text-2xl font-bold text-[#0F172A] mb-1">{(project.budget * 1.21).toFixed(2)} <span className="text-sm font-normal text-[#475569]/60">incl. BTW</span></div>
                            <div className="text-sm text-[#475569] break-words">{project.company.name}</div>
                          </div>
                        </div>

                        {/* Onderste rij: meta wrapt, knoppen op mobiel over de volle breedte (P1) */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 min-w-0">
                            <div className="text-xs sm:text-sm text-[#475569]">
                              <span className="font-medium text-[#0F172A]">{project.contentType}</span>
                              {project.contentFormat && (
                                <span> • {project.contentFormat.join(', ')}</span>
                              )}
                            </div>
                            {project.deliverables && project.deliverables.length > 0 && (
                              <div className="text-xs sm:text-sm text-[#475569] whitespace-nowrap">
                                {project.deliverables.length} deliverables
                              </div>
                            )}
                          </div>

                          <div className="flex flex-wrap items-center gap-2 sm:gap-3 sm:flex-shrink-0">
                            <button
                              onClick={() => onNavigate ? onNavigate('messages') : navigate(`/messages?project=${project.id}`)}
                              className="flex-1 sm:flex-none justify-center min-h-[44px] sm:min-h-0 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition flex items-center gap-2 whitespace-nowrap"
                            >
                              <MessageSquare className="w-4 h-4" />
                              Chat
                            </button>

                            <button
                              onClick={() => !nextAction.disabled && (onNavigate ? onNavigate('project-workspace', project.id) : navigate(`/project-workspace/${project.id}`))}
                              className={`flex-1 sm:flex-none justify-center min-h-[44px] sm:min-h-0 px-6 py-2 rounded-lg transition flex items-center gap-2 text-white font-medium whitespace-nowrap ${nextAction.color}`}
                            >
                              <nextAction.icon className="w-4 h-4" />
                              Open Workspace
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default MyActiveProjectsPage